import * as Sentry from '@sentry/react';

let initialized = false;

/**
 * Initialize Sentry on the browser. Safe to call multiple times — guarded
 * by `initialized` and a real DSN check (placeholders are ignored).
 *
 * Note: the project uses Next.js `output: 'export'` (static export), which is
 * incompatible with `@sentry/nextjs` (no server runtime to instrument). We
 * use `@sentry/react` directly — same browser SDK, fewer assumptions about
 * the framework.
 */
export function initSentry(): void {
  if (initialized) return;
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  // Treat empty / placeholder DSN as "not configured".
  if (!dsn || dsn.includes('PLACEHOLDER') || dsn.includes('examplePublicKey')) return;
  initialized = true;
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE || undefined,
    ignoreErrors: [
      // Browser noise — not actionable.
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      // Network errors we already handle.
      'Network Error',
      'Failed to fetch',
    ],
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    // Only attach sentry-trace/baggage headers to OUR backend.
    // Default Sentry behavior leaks them to every fetch target including
    // third-party iframes (Stripe, MercadoPago, SumUp) which turns simple
    // requests into preflighted CORS and can interact unpredictably with
    // their telemetry endpoints.
    tracePropagationTargets: [
      /^https:\/\/api-56tpojcbbq-uc\.a\.run\.app/,
      /^https:\/\/apexandes\.com/,
      /^https:\/\/www\.apexandes\.com/,
      /^\//, // same-origin relative paths
    ],
    // Filter out noise from third-party telemetry that we can't control.
    denyUrls: [
      /r\.stripe\.com/,
      /m\.stripe\.com/,
      /q\.stripe\.com/,
      /js\.stripe\.com/,
      /sumup\.com\/.*\/telemetry/,
      /mercadopago\.com\/.*\/sdks\//,
      /www\.googletagmanager\.com/,
      /www\.google-analytics\.com/,
    ],
  });
}

export { Sentry };
